import { createPortal } from "react-dom";
import { AlertTriangle, Check, Info, LoaderCircle, Plus, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  AI_REPROCESS_TASKS,
  apiClient,
  type AiReprocessEstimate,
  type AiReprocessItem,
  type AiReprocessRun,
  type AiReprocessTask,
  type BulkTarget
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { POLL_INTERVAL_MS } from "@/shared/hooks/usePolling";
import { markNoticeSeen } from "@/shell/notifications";
import { useDismiss } from "@/shared/hooks/useDismiss";
import {
  diffParts,
  estimateCostLabel,
  estimateSkippedLabel,
  isAiReprocessRunning,
  summarizeSample
} from "@/features/mentions/ai-reprocess-view";
import { AiReprocessProgress } from "@/features/mentions/AiReprocessProgress";
import {
  AiReprocessChangeList,
  AiReprocessItemHead
} from "@/features/mentions/AiReprocessItemCard";

const DEFAULT_TASKS: AiReprocessTask[] = [
  "polarity_intensity",
  "role",
  "themes",
  "key_message"
];

/**
 * Reprocessar com IA as menções escolhidas (2026-10-05).
 *
 * Três passos, e só o último escreve: a ESTIMATIVA aparece sozinha e se
 * refaz a cada mudança de tarefa (sem IA, sem custo); a AMOSTRA
 * classifica algumas menções sorteadas sem gravar e mostra o
 * antes/depois; APLICAR manda o recorte inteiro pra fila. A amostra é
 * opcional, mas é ela que responde "a IA vai fazer o que eu espero?"
 * antes de pagar por milhares de menções — trocar a tarefa a invalida,
 * porque ela não falaria mais do pedido.
 *
 * O aviso de sobrescrita é explícito porque a regra é (decisão da
 * dona): correção manual é reprocessada. O valor anterior não se
 * perde — fica no histórico de cada menção.
 */
export function AiReprocessDialog({
  token,
  count,
  targetLabel,
  target,
  initialSample,
  onApplied,
  onClose
}: {
  token: string;
  /** `null` = ainda não se sabe (prévia reaberta): o título espera a
   *  estimativa. */
  count: number | null;
  targetLabel: string;
  /** O alvo, como no lote — recorte da tela ou ids marcados. */
  target: () => BulkTarget;
  /** A prévia reaberta pelo sino: o diálogo nasce com ela, nas mesmas
   *  tarefas, e aplicar vai pro mesmo recorte. */
  initialSample?: AiReprocessRun;
  onApplied: (run: AiReprocessRun) => void;
  onClose: () => void;
}) {
  const [tasks, setTasks] = useState<AiReprocessTask[]>(
    initialSample?.tasks ?? DEFAULT_TASKS
  );
  const [includeReviewed, setIncludeReviewed] = useState(
    initialSample?.include_reviewed ?? false
  );
  const [estimate, setEstimate] = useState<AiReprocessEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [sample, setSample] = useState<AiReprocessRun | null>(initialSample ?? null);
  // A primeira estimativa NÃO descarta a prévia reaberta: ela é das
  // mesmas tarefas. Trocar a tarefa depois invalida, como sempre.
  const keepInitialSampleRef = useRef(Boolean(initialSample));
  const [sampleItems, setSampleItems] = useState<AiReprocessItem[] | null>(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // O alvo não muda com o diálogo aberto; a função do pai é recriada a
  // cada render dele, então fica guardada.
  const targetRef = useRef(target);
  const busy = applying || (sample !== null && isAiReprocessRunning(sample));

  useDismiss(true, () => {
    if (!applying) onClose();
  });

  // A estimativa acompanha a escolha. Pequeno atraso pra várias caixas
  // marcadas em sequência virarem UMA chamada.
  useEffect(() => {
    if (keepInitialSampleRef.current) {
      keepInitialSampleRef.current = false;
    } else {
      setSample(null);
      setSampleItems(null);
    }
    if (!tasks.length) {
      setEstimate(null);
      return;
    }
    let alive = true;
    setEstimating(true);
    const timer = window.setTimeout(() => {
      apiClient
        .estimateAiReprocess(token, targetRef.current(), { tasks, includeReviewed })
        .then(found => {
          if (alive) {
            setEstimate(found);
            setError(null);
          }
        })
        .catch(failure => {
          if (alive) setError(errorMessage(failure, "Não foi possível estimar."));
        })
        .finally(() => {
          if (alive) setEstimating(false);
        });
    }, 300);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [token, tasks, includeReviewed]);

  // A prévia reaberta já terminada: busca o antes/depois de uma vez. A
  // pessoa está vendo o resultado, então o aviso dela sai do sino.
  useEffect(() => {
    if (!initialSample || isAiReprocessRunning(initialSample)) return;
    let alive = true;
    void markNoticeSeen(token, "ai_reprocess_sample", String(initialSample.id));
    apiClient
      .listAiReprocessItems(token, initialSample.id, { limit: 50 })
      .then(items => {
        if (alive) setSampleItems(items);
      })
      .catch(failure => {
        if (alive) setError(errorMessage(failure, "Não foi possível carregar a prévia."));
      });
    return () => {
      alive = false;
    };
    // Só na abertura: é a prévia com que o diálogo nasceu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A amostra roda na fila rápida; acompanha até ela terminar e então
  // busca o antes/depois.
  useEffect(() => {
    if (!sample || !isAiReprocessRunning(sample)) return;
    let alive = true;
    const timer = window.setTimeout(async () => {
      try {
        const next = await apiClient.getAiReprocess(token, sample.id);
        if (!alive) return;
        setSample(next);
        if (!isAiReprocessRunning(next)) {
          // A pessoa está vendo o resultado: o aviso do sino só fica de
          // pé pra quem saiu da tela antes de terminar (decisão da dona,
          // 2026-10-07). Falhar aqui não atrapalha a amostra.
          void markNoticeSeen(token, "ai_reprocess_sample", String(next.id));
          const items = await apiClient.listAiReprocessItems(token, next.id, { limit: 50 });
          if (alive) setSampleItems(items);
        }
      } catch (failure) {
        if (alive) setError(errorMessage(failure, "Não foi possível acompanhar a prévia."));
      }
    }, POLL_INTERVAL_MS);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [token, sample]);

  const toggleTask = (task: AiReprocessTask) =>
    setTasks(current =>
      current.includes(task) ? current.filter(item => item !== task) : [...current, task]
    );

  const requestSample = async () => {
    setError(null);
    setSampleItems(null);
    try {
      setSample(
        await apiClient.sampleAiReprocess(token, targetRef.current(), {
          tasks,
          includeReviewed
        })
      );
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível gerar a prévia."));
    }
  };

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      onApplied(
        await apiClient.applyAiReprocess(token, targetRef.current(), {
          tasks,
          includeReviewed,
          sampleRunId: sample && !isAiReprocessRunning(sample) ? sample.id : null
        })
      );
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível iniciar o reprocessamento."));
      setApplying(false);
    }
  };

  const eligible = estimate?.eligible ?? 0;
  const headerCount = estimate?.total ?? count;
  const blocked = estimate?.active_run_id != null;
  const skipped = estimate ? estimateSkippedLabel(estimate) : null;
  const sampleRunning = sample !== null && isAiReprocessRunning(sample);
  const sampleFailed = sample?.status === "ERRORED";
  const summary = sampleItems ? summarizeSample(sampleItems) : null;
  // O que não mudaria fica recolhido: a amostra existe pra mostrar o
  // que a IA faria de diferente.
  const others = summary
    ? [...summary.unchanged, ...summary.skipped, ...summary.failed]
    : [];

  return createPortal(
    <div className="modal-overlay" onClick={applying ? undefined : onClose}>
      <div
        className="modal-container bulk-dialog ai-reprocess-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-reprocess-title"
        onClick={event => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">REPROCESSAR COM IA</span>
            <h2 id="ai-reprocess-title">
              {headerCount === null
                ? "Calculando o recorte…"
                : `${headerCount.toLocaleString("pt-BR")} ${headerCount === 1 ? "menção" : "menções"}`}
            </h2>
            <p className="bulk-dialog-target">{targetLabel}</p>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            disabled={applying}
          >
            <X size={18} />
          </button>
        </header>
        <div className="modal-body">
          <div className="ai-reprocess">
            <section className="ai-reprocess-step" aria-labelledby="ai-reprocess-step-tasks">
              <h3 className="ai-reprocess-step-title" id="ai-reprocess-step-tasks">
                <span className="ai-reprocess-step-num" aria-hidden="true">1</span>
                O que a IA deve refazer
              </h3>
              <div className="ai-reprocess-chips" role="group" aria-label="Tarefas">
                {AI_REPROCESS_TASKS.map(task => {
                  const on = tasks.includes(task.value);
                  return (
                    <label
                      key={task.value}
                      className={`ai-reprocess-chip${on ? " is-on" : ""}${busy ? " is-disabled" : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={on}
                        onChange={() => toggleTask(task.value)}
                        disabled={busy}
                      />
                      {on ? (
                        <Check size={14} aria-hidden="true" />
                      ) : (
                        <Plus size={14} aria-hidden="true" />
                      )}
                      {task.label}
                    </label>
                  );
                })}
              </div>
              <label className="filter-check ai-reprocess-reviewed">
                <input
                  type="checkbox"
                  checked={includeReviewed}
                  onChange={event => setIncludeReviewed(event.target.checked)}
                  disabled={busy}
                />
                <span>
                  Incluir menções já marcadas como revisadas
                  <small>Por padrão elas ficam de fora, para não desfazer a revisão de ninguém.</small>
                </span>
              </label>
            </section>

            <div className="ai-reprocess-estimate" aria-live="polite">
              {!tasks.length ? (
                <p className="ai-reprocess-hint">Escolha pelo menos uma tarefa acima.</p>
              ) : estimating && !estimate ? (
                <p className="ai-reprocess-hint">
                  <LoaderCircle className="spin" size={14} aria-hidden="true" /> Calculando
                  quantas menções entram e quanto custa…
                </p>
              ) : estimate ? (
                <>
                  <div className="ai-reprocess-stats">
                    <div className="ai-reprocess-stat">
                      <span className="ai-reprocess-stat-value">
                        {eligible.toLocaleString("pt-BR")}
                        <small> de {estimate.total.toLocaleString("pt-BR")}</small>
                      </span>
                      <span className="ai-reprocess-stat-label">
                        {eligible === 1 ? "menção será reprocessada" : "menções serão reprocessadas"}
                      </span>
                    </div>
                    <div className="ai-reprocess-stat">
                      <span className="ai-reprocess-stat-value">
                        {estimate.estimated_cost === null && eligible > 0
                          ? "—"
                          : estimateCostLabel(estimate)}
                      </span>
                      <span className="ai-reprocess-stat-label">custo estimado</span>
                    </div>
                  </div>
                  {skipped && <p className="ai-reprocess-hint">{skipped}</p>}
                  {estimate.estimated_cost === null && eligible > 0 && (
                    <div className="account-feedback warning" role="status">
                      <AlertTriangle size={14} aria-hidden="true" /> Sem base para
                      estimar o custo: esta empresa não tem consumo de IA com preço
                      nos últimos 30 dias. Dá para aplicar mesmo assim — rode a
                      prévia antes para ter uma ideia do resultado.
                    </div>
                  )}
                  {estimate.estimated_cost !== null && eligible > 0 && (
                    <p className="ai-reprocess-hint">
                      Pelo custo médio de{" "}
                      {estimate.cost_basis_calls.toLocaleString("pt-BR")} chamadas dos
                      últimos 30 dias. É um teto: o que a regra determinística resolver
                      não chama a IA.
                    </p>
                  )}
                </>
              ) : null}
            </div>

            {blocked && (
              <div className="account-feedback error" role="status">
                <AlertTriangle size={14} aria-hidden="true" /> Já existe um
                reprocessamento em andamento nesta empresa. Aguarde ele terminar.
              </div>
            )}

            <section className="ai-reprocess-step" aria-labelledby="ai-reprocess-step-sample">
              <div className="ai-reprocess-step-head">
                <h3 className="ai-reprocess-step-title" id="ai-reprocess-step-sample">
                  <span className="ai-reprocess-step-num" aria-hidden="true">2</span>
                  Confira uma prévia
                  <span className="ai-reprocess-optional">opcional</span>
                </h3>
                {summary && !sampleRunning && (
                  <button
                    type="button"
                    className="secondary-button ai-reprocess-sample-button"
                    onClick={() => void requestSample()}
                    disabled={!eligible || busy}
                  >
                    <Sparkles size={15} aria-hidden="true" />
                    Gerar outra prévia
                  </button>
                )}
              </div>

              {sample && sampleRunning ? (
                <SampleProgress run={sample} />
              ) : sample && !sampleItems && !sampleFailed ? (
                <div className="ai-reprocess-sample-empty" aria-live="polite">
                  <LoaderCircle className="spin" size={20} aria-hidden="true" />
                  <strong>Carregando o resultado…</strong>
                </div>
              ) : !summary ? (
                <div className="ai-reprocess-sample-empty">
                  <span className="ai-reprocess-sample-icon" aria-hidden="true">
                    <Sparkles size={18} />
                  </span>
                  <div>
                    <strong>Veja a IA em ação antes de aplicar</strong>
                    <p>
                      Sorteamos algumas menções do recorte e mostramos como elas
                      ficariam. Nada é gravado.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="secondary-button ai-reprocess-sample-button"
                    onClick={() => void requestSample()}
                    disabled={!eligible || busy}
                  >
                    <Sparkles size={15} aria-hidden="true" />
                    {sampleFailed ? "Tentar de novo" : "Ver prévia"}
                  </button>
                </div>
              ) : null}

              {sampleFailed && (
                <div className="account-feedback error">
                  A prévia falhou{sample?.message ? `: ${sample.message}` : "."}
                </div>
              )}

              {summary && sampleItems && !sampleRunning && (
                <>
                  <p className="ai-reprocess-sample-summary" aria-live="polite">
                    Das <strong>{sampleItems.length}</strong>{" "}
                    {sampleItems.length === 1 ? "menção sorteada" : "menções sorteadas"}:
                    <span className="badge-pill active">
                      {summary.changed.length}{" "}
                      {summary.changed.length === 1 ? "mudaria" : "mudariam"}
                    </span>
                    <span className="badge-pill inactive">
                      {summary.unchanged.length} sem mudança
                    </span>
                    {summary.skipped.length > 0 && (
                      <span className="badge-pill warning">
                        {summary.skipped.length}{" "}
                        {summary.skipped.length === 1 ? "pulada" : "puladas"}
                      </span>
                    )}
                    {summary.failed.length > 0 && (
                      <span className="badge-pill error">
                        {summary.failed.length} com erro
                      </span>
                    )}
                  </p>

                  {summary.changed.length === 0 ? (
                    <p className="ai-reprocess-hint">
                      Nestas menções a IA manteria tudo como está. Gere outra prévia
                      ou ajuste as tarefas se esperava alguma mudança.
                    </p>
                  ) : (
                    <ul className="ai-reprocess-sample-list">
                      {summary.changed.map(item => (
                        <li key={item.mention_id} className="ai-reprocess-card">
                          <AiReprocessItemHead item={item} />
                          <AiReprocessChangeList parts={diffParts(item.diff)} />
                          {item.message && <em>{item.message}</em>}
                        </li>
                      ))}
                    </ul>
                  )}

                  {others.length > 0 && (
                    <details className="ai-reprocess-others">
                      <summary>
                        {others.length === 1
                          ? "Ver a que não mudaria"
                          : `Ver as ${others.length} que não mudariam`}
                      </summary>
                      <ul>
                        {others.map(item => (
                          <li key={item.mention_id}>
                            <AiReprocessItemHead item={item} />
                            {item.message && <em>{item.message}</em>}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </>
              )}
            </section>

            <div className="ai-reprocess-callout">
              <Info size={16} aria-hidden="true" />
              <p>
                <strong>Isso substitui a classificação atual</strong> nas tarefas
                escolhidas, inclusive correções feitas à mão. O valor anterior fica
                no histórico de cada menção, em seu nome. Rascunhos de amostra do
                período são recalculados sozinhos; amostras salvas só mudam se
                alguém pedir “Atualizar dados”.
              </p>
            </div>
            {error && (
              <div className="account-feedback error" aria-live="polite">
                {error}
              </div>
            )}
          </div>
        </div>
        <footer className="modal-footer">
          <button
            type="button"
            className="secondary-button"
            onClick={onClose}
            disabled={applying}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => void apply()}
            disabled={!eligible || blocked || busy || estimating}
          >
            {applying ? (
              <LoaderCircle className="spin" size={16} />
            ) : (
              `Reprocessar ${eligible.toLocaleString("pt-BR")} ${eligible === 1 ? "menção" : "menções"}`
            )}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}

/** A amostra andando: quantas das sorteadas a IA já classificou. */
function SampleProgress({ run }: { run: AiReprocessRun }) {
  return (
    <div className="ai-reprocess-sample-running" aria-live="polite">
      <p className="ai-reprocess-sample-running-head">
        <LoaderCircle className="spin" size={16} aria-hidden="true" />
        <strong>Classificando a prévia…</strong>
        <span>Pode fechar: o sino avisa quando terminar.</span>
      </p>
      <AiReprocessProgress run={run} label="Prévia" />
    </div>
  );
}
